import { CACHE_TIME_3_DAYS } from 'src/Cache/RedisCache';
import RedisCaching from './RedisCaching';
import DoNotLogInSentryError from './DoNotLogInSentryError';

const { compress, decompress } = require('shrink-string');

// Type name
export const EQUITY = "Equity";
export const FUND = "Fund";
export const ETF = "ETF";
export const CEF = "CEF";
export const INDEX = "Index";

// Exchange code
export const LSE = "LSE";
export const XDJI = "XDJI";
export const XNAS = "XNAS";

export default class Graphql {

    static async getQuote(marketCode, type) {
        const query = `query pageData($identifier: String!, $includeFigaroData: Boolean) {
            ${type ? `favorites(marketCode: $identifier, types: ${type}) {
                name
            }` : ``}
            quote(identifier: $identifier, includeFigaroData: $includeFigaroData) {
                __typename
                name
                nameShort              
                marketCode    
                exchangeCode            
                dayChange
                dayChangePct

                figaroData {
                    isaEligibility
                    sippEligibility
                }
        
                ... on Equity {
                    currency
                    isin
                    sedol
                    additionalName
                    askPrice
                    bidPrice
                    bidPriceTime
                    bidPriceDate
                    lastPriceDate
                    volume
                    cumulativeVolume
                    dividendYield
                    peRatio
                    openPrice
                    closePrice
                    yearHighPrice
                    yearLowPrice
                    marketCap
                    dayHighPrice
                    sectorName
                    symbol
                }
                ... on Fund {
                    isin
                    sedol
                    currency
                    lastPrice
                    lastPriceDate
                    latestNavPrice
                    latestNavDate
                    pricingFrequency
                    ongoingCharge
                    ucits
                    instrumentStructure
                    sectorName
                    yield  
                    rating 
                    dealingCutOffTime
                    incomeFrequency
                }

                ... on ETF {
                    name
                    isin
                    sedol
                    currency
                    askPrice
                    bidPrice
                    bidPriceTime
                    bidPriceDate
                    lastPriceDate
                    volume
                    cumulativeVolume
                    openPrice
                    closePrice
                    yearHighPrice
                    yearLowPrice
                    marketCap
                    dayHighPrice
                    ongoingCharge
                    ucits
                    instrumentType
                    rating 
                    yield
                    replicationMethod
                    incomeFrequency
                }

                ... on CEF {
                    isin
                    sedol
                    currency
                    additionalName
                    askPrice
                    bidPrice
                    bidPriceTime
                    bidPriceDate
                    lastPriceDate
                    volume
                    cumulativeVolume
                    dividendYield
                    openPrice
                    closePrice
                    yearHighPrice
                    yearLowPrice
                    marketCap
                    dayHighPrice
                    estimatedNavPrice
                    latestNavPrice
                    latestNavDate
                    premiumDiscount
                    ongoingCharge
                    rating 
                    yearHighNav
                    yearLowNav
                    calendarYearHigh
                    calendarYearLow
                    discountYearHigh
                    discountYearLow
                    sharesInIssue
                    symbol
                }

                ... on Index {
                    lastPrice
                    indexDayHighPrice: dayHighPrice
                    indexDayLowPrice: dayLowPrice
                    indexOpenPrice: openPrice
                  	indexClosePrice: closePrice
                    indexYearLowPrice: yearLowPrice
                    indexYearHighPrice: yearHighPrice
                }
            }
            instrumentInfo(identifier: $identifier) {
                type
                msid
            }
            securitySummary(marketCode: $identifier) {
                marketCode
            }
        }`;

        const data = await Graphql.fetch(query, {
            identifier: marketCode,
            includeFigaroData: true
        });

        // We have to do this as graphql changes LSE:AV. to LSE:AV/
        if (data.quote) {
            data.quote.marketCode = marketCode;
            data.quote.symbol = marketCode.split(':')[1];
        }

        return data;
    }

    static async getFavourites() {
        const query = `query {
            favorites(types: [FAVORITE_FUNDS, AJB_FUND, IT_SELECT_LIST]) {
                sedol
                belongsTo
            }
        }`;

        const data = await Graphql.fetch(query);

        return data.favorites;
    }

    static async fetch(query, variables) {
        const cache = new RedisCaching;
        const requestBody = JSON.stringify({ query, variables });
        const compressedRequestBody = await compress(requestBody);
        const settings = {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: requestBody
        };

        const response = await fetch(process.env.GRAPHQL_ENDPOINT, settings);

        if (!response.ok) {
            let compressedCachedResponse = await cache.get(compressedRequestBody);

            if (compressedCachedResponse) {
                const decompressedCacheResponse = await decompress(compressedCachedResponse);
                return JSON.parse(decompressedCacheResponse);
            }
            else {
                throw new Error('GraphQL lookup failed and response is not cached.');
            }
        }

        const json = await response.json();

        if (json.errors) {
            const error = json.errors[0];
            if (error?.message?.startsWith('Instrument cannot be found')) {
                return {
                    notFound: true,
                    quote: {}
                };
            }
            throw new DoNotLogInSentryError(error.message);
        }
        else {
            let compressedData = await compress(JSON.stringify(json.data));
            await cache.set(compressedRequestBody, CACHE_TIME_3_DAYS, compressedData);
        }

        return json.data;
    }

}